import React, { useState } from "react";
import { ErrorMessage } from "components/Messages"
import BaseAlert from "components/Alert/BaseAlert"
import BaseButton from "components/Button/BaseButton"
import BaseInput from "components/Inputs/BaseInput";
import { AUTH_STEP } from "app/Login/hooks/useCognitoAuth";
import { useLanguage } from "context/LanguageContext";

import "./loginwindow.scss";

const MFA_LABEL = {
    SMS: "auth.sms",
    TOTP: "auth.totp",
};

const E164 = /^\+[1-9]\d{7,14}$/;

const inputProps = {
    mainColor: "var(--text-primary)",
    labelColor: "var(--text-muted)",
    fullWidth: true,
    height: 45,
};


const AuthForm = ({ title, subtitle, onSubmit, children, error, notice }) => {
    const { t } = useLanguage();
    return (
        <form className="LoginContent" onSubmit={onSubmit} noValidate>
            <h1 className="title">{title}</h1>
            {subtitle && <p className="subtitle">{subtitle}</p>}
            {children}
            {notice && <BaseAlert message={t(notice)} />}
            {error && <ErrorMessage message={t(error)}/>}
        </form>
    );
};

const BackButton = ({ onClick, busy }) => {
    const { t } = useLanguage();
    return (
        <BaseButton type="button" data-test="auth-back" onClick={onClick} disabled={busy}>
            {t("common.back")}
        </BaseButton>
    );
};


const SignInStep = ({ auth }) => {
    const { t } = useLanguage();
    const [email, setEmail] = useState(auth.accountEmail);
    const [password, setPassword] = useState("");
    const [localError, setLocalError] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email) {
            setLocalError("auth.emailRequired");
            return;
        }
        if (!password) {
            setLocalError("auth.passwordRequired");
            return;
        }
        setLocalError("");
        auth.signInWithPassword(email, password);
    };

    return (
        <AuthForm
            title={t("auth.signIn")}
            subtitle={t("auth.signInSubtitle")}
            onSubmit={handleSubmit}
            error={localError || auth.error}
            notice={auth.notice}
        >
            <BaseInput
                dataTest="email-input"
                {...inputProps}
                label={t("common.email")}
                type="email"
                placeholder="user@example.com"
                inputValue={email}
                setInputValue={setEmail}
                pattern={{ autoComplete: "username" }}
                required
            />
            <BaseInput
                dataTest="password-input"
                {...inputProps}
                label={t("common.password")}
                type="password"
                placeholder="******"
                inputValue={password}
                setInputValue={setPassword}
                pattern={{ autoComplete: "current-password" }}
                required
            />
            <BaseButton data-test="login-button" disabled={auth.busy}>{t("auth.login")}</BaseButton>
            <BaseButton type="button" data-test="forgot-password" onClick={auth.goToReset} disabled={auth.busy}>
                {t("auth.forgotPassword")}
            </BaseButton>
        </AuthForm>
    );
};

const ConfirmEmailStep = ({ auth }) => {
    const { t } = useLanguage();
    const [code, setCode] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!code) {
            return;
        }
        auth.confirmSignUp(code);
    };

    return (
        <AuthForm
            title={t("auth.confirmEmail")}
            subtitle={t("auth.confirmEmailSubtitle", { email: auth.accountEmail || t("auth.yourEmail") })}
            onSubmit={handleSubmit}
            error={auth.error}
            notice={auth.notice}
        >
            <BaseInput
                dataTest="confirm-email-code"
                {...inputProps}
                label={t("auth.confirmationCode")}
                type="text"
                placeholder="123456"
                inputValue={code}
                setInputValue={setCode}
                pattern={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                required
            />
            <BaseButton data-test="confirm-email-button" disabled={auth.busy}>{t("common.confirm")}</BaseButton>
            <div className="authLinks">
                <BaseButton type="button" data-test="resend-code" onClick={auth.resendCode} disabled={auth.busy}>
                    {t("auth.resendCode")}
                </BaseButton>
                <BackButton onClick={auth.goToSignIn} busy={auth.busy}/>
            </div>
        </AuthForm>
    );
};

const MfaChoiceStep = ({ auth, title, subtitle, onChoose }) => {
    const { t } = useLanguage();
    return (
        <AuthForm title={title} subtitle={subtitle} onSubmit={(e) => e.preventDefault()} error={auth.error} notice={auth.notice}>
            <div className="mfaChoices">
                {auth.methods.map((method) => (
                    <BaseButton
                        key={method}
                        type="button"
                        data-test={method === "SMS" ? "mfa-sms" : "mfa-totp"}
                        onClick={() => onChoose(method)}
                        disabled={auth.busy}
                    >
                        {t(MFA_LABEL[method])}
                    </BaseButton>
                ))}
            </div>
            <BackButton onClick={auth.goToSignIn} busy={auth.busy}/>
        </AuthForm>
    );
};

const SmsSetupStep = ({ auth }) => {
    const { t } = useLanguage();
    const [phone, setPhone] = useState("");
    const [localError, setLocalError] = useState("");
    const back = auth.mustConfigureMfa ? auth.backToMfaChoice : auth.goToSignIn;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!auth.mustConfigureMfa) {
            auth.sendSmsSetup();
            return;
        }
        if (!E164.test(phone.trim())) {
            setLocalError("auth.phoneInvalid");
            return;
        }
        setLocalError("");
        auth.sendSmsSetup(phone.trim());
    };

    return (
        <AuthForm
            title={t("auth.setupSms")}
            subtitle={auth.mustConfigureMfa
                ? t("auth.setupSmsRequired")
                : t("auth.setupSmsOptional")}
            onSubmit={handleSubmit}
            error={localError || auth.error}
            notice={auth.notice}
        >
            {auth.mustConfigureMfa && (
                <BaseInput
                    dataTest="mfa-sms-phone"
                    {...inputProps}
                    label={t("auth.phone")}
                    type="tel"
                    placeholder="+48123456789"
                    inputValue={phone}
                    setInputValue={setPhone}
                    required
                />
            )}
            <BaseButton data-test="sms-setup-send" disabled={auth.busy}>{t("auth.sendCode")}</BaseButton>
            <BackButton onClick={back} busy={auth.busy}/>
        </AuthForm>
    );
};

const CodeStep = ({ auth, title, subtitle, method, onSubmit, onResend, onBack }) => {
    const { t } = useLanguage();
    const [code, setCode] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!code) {
            return;
        }
        onSubmit(code, method);
    };

    return (
        <AuthForm title={title} subtitle={subtitle} onSubmit={handleSubmit} error={auth.error} notice={auth.notice}>
            <BaseInput
                dataTest="mfa-code-input"
                {...inputProps}
                label={t("auth.verificationCode")}
                type="text"
                placeholder="123456"
                inputValue={code}
                setInputValue={setCode}
                pattern={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                required
            />
            <BaseButton data-test="mfa-confirm" disabled={auth.busy}>{t("common.verify")}</BaseButton>
            <div className="authLinks">
                {onResend && (
                    <BaseButton type="button" data-test="resend-code" onClick={onResend} disabled={auth.busy}>
                        {t("auth.resendCode")}
                    </BaseButton>
                )}
                <BackButton onClick={onBack || auth.goToSignIn} busy={auth.busy}/>
            </div>
        </AuthForm>
    );
};

const TotpSetupStep = ({ auth }) => {
    const { t } = useLanguage();
    const [code, setCode] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!code) {
            return;
        }
        auth.confirmTotp(code);
    };

    return (
        <AuthForm
            title={t("auth.setupTotp")}
            subtitle={t("auth.setupTotpSubtitle")}
            onSubmit={handleSubmit}
            error={auth.error}
            notice={auth.notice}
        >
            {auth.qrCode && (
                <img className="qrCode" src={auth.qrCode} alt={t("auth.qrAlt")}/>
            )}
            {auth.secret && <p className="secretCode">{auth.secret}</p>}
            <BaseInput
                dataTest="totp-setup-code"
                {...inputProps}
                label={t("auth.authenticatorCode")}
                type="text"
                placeholder="123456"
                inputValue={code}
                setInputValue={setCode}
                pattern={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                required
            />
            <BaseButton data-test="totp-setup-confirm" disabled={auth.busy}>{t("common.verify")}</BaseButton>
            <BackButton onClick={auth.mustConfigureMfa ? auth.backToMfaChoice : auth.goToSignIn} busy={auth.busy}/>
        </AuthForm>
    );
};

const NewPasswordStep = ({ auth }) => {
    const { t } = useLanguage();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [localError, setLocalError] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!password || !confirmPassword) {
            setLocalError("auth.passwordRequired");
            return;
        }
        if (password !== confirmPassword) {
            setLocalError("auth.passwordsMismatch");
            return;
        }
        setLocalError("");
        auth.submitNewPassword(password);
    };

    return (
        <AuthForm
            title={t("auth.newPassword")}
            subtitle={t("auth.newPasswordSubtitle")}
            onSubmit={handleSubmit}
            error={localError || auth.error}
            notice={auth.notice}
        >
            <BaseInput
                dataTest="new-password"
                {...inputProps}
                label={t("auth.newPasswordLabel")}
                type="password"
                placeholder="******"
                inputValue={password}
                setInputValue={setPassword}
                pattern={{ autoComplete: "new-password" }}
                required
            />
            <BaseInput
                dataTest="confirm-new-password"
                {...inputProps}
                label={t("auth.confirmPassword")}
                type="password"
                placeholder="******"
                inputValue={confirmPassword}
                setInputValue={setConfirmPassword}
                pattern={{ autoComplete: "new-password" }}
                required
            />
            <BaseButton data-test="new-password-button" disabled={auth.busy}>{t("auth.savePassword")}</BaseButton>
        </AuthForm>
    );
};

const ResetRequestStep = ({ auth }) => {
    const { t } = useLanguage();
    const [email, setEmail] = useState(auth.accountEmail);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email) {
            return;
        }
        auth.sendResetCode(email);
    };

    return (
        <AuthForm
            title={t("auth.resetPassword")}
            subtitle={t("auth.resetPasswordSubtitle")}
            onSubmit={handleSubmit}
            error={auth.error}
            notice={auth.notice}
        >
            <BaseInput
                dataTest="reset-email"
                {...inputProps}
                label={t("common.email")}
                type="email"
                placeholder="user@example.com"
                inputValue={email}
                setInputValue={setEmail}
                required
            />
            <BaseButton data-test="reset-send" disabled={auth.busy}>{t("auth.sendCode")}</BaseButton>
            <BackButton onClick={auth.goToSignIn} busy={auth.busy}/>
        </AuthForm>
    );
};

const ResetCodeStep = ({ auth }) => {
    const { t } = useLanguage();
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [localError, setLocalError] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!code || !password) {
            setLocalError("auth.codeAndPasswordRequired");
            return;
        }
        if (password !== confirmPassword) {
            setLocalError("auth.passwordsMismatch");
            return;
        }
        setLocalError("");
        auth.submitReset(code, password);
    };

    return (
        <AuthForm
            title={t("auth.newPassword")}
            subtitle={auth.destination
                ? t("auth.resetCodeSent", { destination: auth.destination })
                : t("auth.resetCodeFallback")}
            onSubmit={handleSubmit}
            error={localError || auth.error}
            notice={auth.notice}
        >
            <BaseInput
                dataTest="reset-code"
                {...inputProps}
                label={t("auth.resetCode")}
                type="text"
                placeholder="123456"
                inputValue={code}
                setInputValue={setCode}
                pattern={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                required
            />
            <BaseInput
                dataTest="reset-password"
                {...inputProps}
                label={t("auth.newPasswordLabel")}
                type="password"
                placeholder="******"
                inputValue={password}
                setInputValue={setPassword}
                pattern={{ autoComplete: "new-password" }}
                required
            />
            <BaseInput
                dataTest="reset-confirm-password"
                {...inputProps}
                label={t("auth.confirmPassword")}
                type="password"
                placeholder="******"
                inputValue={confirmPassword}
                setInputValue={setConfirmPassword}
                pattern={{ autoComplete: "new-password" }}
                required
            />
            <BaseButton data-test="reset-confirm" disabled={auth.busy}>{t("auth.updatePassword")}</BaseButton>
            <BackButton onClick={auth.goToSignIn} busy={auth.busy}/>
        </AuthForm>
    );
};


const LoginWindow = ({ auth }) => {
    const { t } = useLanguage();
    if (auth.step === AUTH_STEP.CONFIRM_EMAIL) {
        return (
            <div className="LoginWindow">
                <ConfirmEmailStep auth={auth}/>
            </div>
        );
    }
    if (auth.step === AUTH_STEP.SELECT_MFA) {
        return (
            <div className="LoginWindow">
                <MfaChoiceStep
                    auth={auth}
                    title={t("auth.chooseVerification")}
                    subtitle={t("auth.chooseVerificationSubtitle")}
                    onChoose={auth.chooseMfa}
                />
            </div>
        );
    }
    if (auth.step === AUTH_STEP.SETUP_MFA) {
        return (
            <div className="LoginWindow">
                <MfaChoiceStep
                    auth={auth}
                    title={t("auth.setupVerification")}
                    subtitle={t("auth.setupVerificationSubtitle")}
                    onChoose={auth.chooseSetup}
                />
            </div>
        );
    }
    if (auth.step === AUTH_STEP.SMS_SETUP) {
        return (
            <div className="LoginWindow">
                <SmsSetupStep auth={auth}/>
            </div>
        );
    }
    if (auth.step === AUTH_STEP.SMS_CODE) {
        const subtitle = auth.destination
            ? t("auth.enterCodeSent", { destination: auth.destination })
            : t("auth.enterCode");
        return (
            <div className="LoginWindow">
                <CodeStep
                    auth={auth}
                    title={t("auth.smsCode")}
                    subtitle={subtitle}
                    method="SMS"
                    onSubmit={auth.confirmCode}
                    onResend={auth.resendSmsCode}
                    onBack={auth.mustConfigureMfa ? auth.backToMfaChoice : undefined}
                />
            </div>
        );
    }
    if (auth.step === AUTH_STEP.TOTP_CODE) {
        return (
            <div className="LoginWindow">
                <CodeStep
                    auth={auth}
                    title={t("auth.totpCode")}
                    subtitle={t("auth.totpCodeSubtitle")}
                    method="TOTP"
                    onSubmit={auth.confirmCode}
                />
            </div>
        );
    }
    if (auth.step === AUTH_STEP.TOTP_SETUP) {
        return (
            <div className="LoginWindow">
                <TotpSetupStep auth={auth}/>
            </div>
        );
    }
    if (auth.step === AUTH_STEP.NEW_PASSWORD) {
        return (
            <div className="LoginWindow">
                <NewPasswordStep auth={auth}/>
            </div>
        );
    }
    if (auth.step === AUTH_STEP.RESET_REQUEST) {
        return (
            <div className="LoginWindow">
                <ResetRequestStep auth={auth}/>
            </div>
        );
    }
    if (auth.step === AUTH_STEP.RESET_CODE) {
        return (
            <div className="LoginWindow">
                <ResetCodeStep auth={auth}/>
            </div>
        );
    }

    return (
        <div className="LoginWindow">
            <SignInStep auth={auth}/>
        </div>
    );
};

export default LoginWindow;
